// SPDX-License-Identifier: MIT
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import type { MagnifyResult } from '../auth/magnify';
import { renderNextStepsModal } from './next-steps-modal';
import { renderMiseEnLigneWizard, type BaseModel, type BackendModel, type DeployOutcome, type DeployStatus } from './mise-en-ligne-wizard';
import { deployFrontendEnabled } from '../config/deploy';
import { renderProjectAttachments } from './project-attachments';
import { renderDiscoveryPanel } from './discovery-panel';
import { mountFoldedDiscovery } from '../onboarding/folded-discovery-driver';
import { renderReadinessGauge } from './readiness-gauge';
import { renderBuildBeacon } from './build-beacon';
import { readiness } from '../onboarding/readiness';
import { renderForgejoNextSteps } from './forgejo-next-steps';
import type { DiscoveryResult } from '../projects/discover';
import type { ProjectVersionRow, VersionContextKind } from '../projects/api';

// Guided first-run (beat ⑤, §5.9) — the readiness gauge on the (collapsed) plan +
// the single « ✦ Construis-le » next-step beacon. A SELF-CONTAINED, block-level,
// self-scoping (.gfr) spine appended to the fiche box → it stacks cleanly and can
// never overlap the existing card content (the operator's « ça se chevauche » guard).
// Honest readiness from the real signal the user provided (answers + outline + stack).
function buildGuidedSpine(opts: ProjectPreviewCardOptions): HTMLElement {
  const r = readiness({
    answersCount: Object.keys(opts.storedAnswers ?? {}).length,
    outlineCount: opts.result.outline.length,
    hasStack: !!opts.result.stack,
    hasFirstSteps: (opts.result.first_steps?.length ?? 0) > 0,
  });
  const wrap = document.createElement('div');
  wrap.className = 'gfr ppc-guided-spine';
  wrap.setAttribute('data-component', 'guided-spine');
  wrap.appendChild(renderReadinessGauge(r));
  wrap.appendChild(renderBuildBeacon({ ready: r.ready, onBuild: () => opts.onBeaconBuild?.() }));
  return wrap;
}

// Guided first-run (beat ④) — the SAME deep-discovery callbacks, presented as the
// folded conversational surface instead of the textarea-wall panel. Used only when
// opts.guided is set (the run started on the guided deposit-box entry); the
// non-guided path keeps renderDiscoveryPanel byte-for-byte. cf folded-discovery-driver.
function foldedDiscoverySurface(
  opts: ProjectPreviewCardOptions,
  discovery: DiscoveryResult,
  initialAnswers?: Record<string, string>,
): HTMLElement {
  return mountFoldedDiscovery({
    discovery,
    initialAnswers,
    onSaveDraft: opts.onSaveDraft ?? (async () => { /* persistence is optional */ }),
    onFinish: async (answers) => { await opts.onFinishDiscovery!(answers, discovery); },
    onRestart: opts.onRestartDiscovery ?? (async () => { /* restart is optional */ }),
  });
}

export interface ProjectPreviewCardOptions {
  result: MagnifyResult;
  onRegenerate?: () => void;
  // Callback fired when the user clicks "💾 Sauvegarder". Caller persists
  // (typically via projects/api saveProject) and resolves on success or
  // rejects on failure ; the button reflects the state visually. If absent,
  // the Save button is not rendered — e.g. when reopening an already-saved
  // card from the "Mes projets" panel, re-saving would be a no-op.
  onSave?: () => Promise<void>;
  // Original user prompt — needed to build the zip's README / CLAUDE.md
  // with the "> idea" blockquote. Optional : when reopening a saved card
  // from "Mes projets" the prompt comes from the saved row's idea column.
  // Defaults to the result.title if omitted (still readable, less rich).
  idea?: string;
  // Pre-saved state. When true, the Save button renders as "✓ Sauvegardé"
  // (disabled) on first render — used by auto-save in /welcome to avoid a
  // visual idle-then-flip flash right after Magnify success.
  initialSaved?: boolean;
  // When both are set, an "Attachments" section is appended after the action
  // row : drop-zone for new files + list of previously uploaded ones. Storage
  // path is `<userId>/<projectId>/...` so RLS gates per-user.
  userId?: string;
  projectId?: string;
  // Layout B (2026-05-24) : when provided, the "context/secondary" blocks
  // (Fichiers attachés + Backend config) are appended into this rail element
  // instead of the card body, so the open-project view splits into a left
  // rail + right work column. Discovery + the fiche/ship stay in the card
  // (main). When absent, everything stacks in the card as before.
  railTarget?: HTMLElement;
  // Layout B (2026-05-24, fast-follow) : the rail order in the open-project
  // view is back → files → online → upsell → backend. Backend must land AFTER
  // online + upsell, but the card builds before the caller appends those — so
  // the caller passes a pre-positioned slot here and we fill it. Falls back to
  // railTarget (then root) when absent, preserving the pre-slot behaviour.
  backendTarget?: HTMLElement;
  // Layout B (2026-05-24, fast-follow) : when true the result card box is
  // wrapped behind a collapsible "fiche" artefact — a pinned summary (title +
  // one-line outline/stack) with a "déplier la fiche complète + actions →"
  // toggle. Découverte + chat stay visible siblings below. When false (the
  // default, and every pre-layout-B call site) the card box shows expanded as
  // before, with no artefact chrome.
  collapsible?: boolean;
  // Only meaningful with collapsible : start expanded (full card visible) vs
  // collapsed (pinned summary). Defaults to false. The caller opens FRESH
  // generations / regenerations expanded (you want to see what changed) and
  // re-opens from "Mes projets" collapsed (the pinned-summary default).
  expanded?: boolean;
  // Only meaningful with collapsible : when true, the fiche synthèse AND the
  // plan body both start collapsed (so a just-delivered desktop project gives
  // room to the agent/build panel). The existing toggle headers remain fully
  // functional — the user can re-open each section independently afterward.
  // Takes precedence over `expanded` (collapsed wins if both are set).
  collapsed?: boolean;
  // Chemise d'atelier (mock zone-projet-v2, operator-validated 2026-06-11).
  // When set (and collapsible), the card builds the COVER (folder tab + serif
  // title + fil d'état + the single grouped toolbar) and renders fiche / plan /
  // attachments / discovery / backend as UNIFORM ledger sections (native
  // <details>, one shared chrome). The cover carries data the card itself
  // doesn't own — the caller computes it from the project row. Absent → the
  // legacy collapsible structure is preserved byte-identical (other call sites
  // + existing tests). welcome.ts MOVES [data-component="project-cover"] to its
  // full-width host after render (same travel pattern as the discovery node).
  cover?: {
    createdAt?: string;
    locale?: 'fr' | 'en';   // tab date formatting — falls back to the browser locale
    built: boolean;        // delivered locally (desktop) or pushed (web)
    dbActive: boolean;     // backend_kind set
    online: 'live' | 'building' | 'failed' | null; // deploy_status
    onlineUrl: string;     // bare host — deploy host when live, else projected shortHandle
  };
  // Honest counts for the ledger section hints (caller derives from the row).
  ledgerHints?: { planCount?: number; discoveryCount?: number };
  // Callback fired when the user clicks "↻ Régénérer enrichi" — caller pulls
  // the latest attachments context and calls Magnify with it, replacing the
  // card content with the new result. Optional ; rendered only when set.
  onRegenerateEnriched?: () => Promise<void>;
  // Forgejo bootstrap : when set, the card renders a "🚀 Push to Forgejo"
  // button. On click, the callback resolves with the resulting repo URL ;
  // the button mutates in place to a "✓ On Forgejo" link to that URL.
  // Rendered only when projectId is set (the project must be saved first).
  onPushForgejo?: () => Promise<{ html_url: string; clone_token?: string | null }>;
  // Initial state : when truthy the button renders as a link to that URL
  // from first paint (matches the behaviour when a project re-opened from
  // Mes Projets already has forgejo_repo_url stored).
  forgejoRepoUrl?: string | null;
  // Read-only clone token (cf gitclone volet ②) → the next-steps clone command
  // authenticates against the login-walled instance, automatic for all users.
  forgejoCloneToken?: string | null;
  // Conversational refinement (2026-05-16 v16) — triggers the
  // discover-attachments edge fn (multimodal Claude scan of the user's
  // uploaded files), then asks 3-5 focused questions before letting the
  // user re-magnify with their answers as additional context.
  //
  // onStartDiscovery is the network call (returns the discovery JSON).
  // onFinishDiscovery is what runs when the user submits answers — caller
  // typically re-calls magnify with attachments_context augmented with
  // discovery + Q&A, then replaces the whole card with the new manifest.
  // Both are optional ; rendered only when projectId is set AND
  // onStartDiscovery is provided.
  onStartDiscovery?: () => Promise<DiscoveryResult>;
  onFinishDiscovery?: (
    answers: Record<string, string>,
    discovery: DiscoveryResult,
  ) => Promise<void>;
  // 2026-05-22 — draft autosave for discovery answers, decoupled from the
  // re-magnify submit. Threaded into renderDiscoveryPanel (both restore +
  // fresh paths) so typed answers survive a reload/navigation. Persists via
  // saveDiscoveryAnswers (the caller wires it). See discovery-panel onSaveDraft.
  onSaveDraft?: (answers: Record<string, string>) => Promise<void>;
  // 2026-05-18 v26 — restore path. When set, the card renders the
  // discovery-panel directly from mount (skipping the "🔍 Genesis te
  // pose des questions" CTA + revealedOnUpload gate) and pre-fills the
  // answer textareas with storedAnswers. The user lands on a panel that
  // looks like "you've already answered these, edit and resubmit when
  // ready". A "🔄 Refaire la découverte" link offers an escape hatch
  // to trigger a fresh Anthropic scan (via onRestartDiscovery).
  storedDiscovery?: DiscoveryResult;
  storedAnswers?: Record<string, string>;
  onRestartDiscovery?: () => Promise<void>;
  // Guided first-run (beat ④): present the deep discovery as the folded
  // conversational surface (deposit → analyse → questions one-at-a-time →
  // régénération versionnée) instead of the textarea-wall panel. Additive —
  // when false/absent the discovery surface is unchanged. cf folded-discovery-driver.
  guided?: boolean;
  // Guided first-run (beat ⑤, §5.9): the « ✦ Construis-le » beacon's action — the
  // single surfaced next-step. Welcome wires it to the build/deliver zone (scroll).
  onBeaconBuild?: () => void;
  // 2026-05-17 v17 — version chip + prev/next nav. When provided, the
  // card header shows "v3 / 5" with arrows ; clicking an arrow calls
  // onSwitchVersion with the target version_number, and the parent is
  // responsible for re-rendering the card with that version's content.
  currentVersion?: number;
  versions?: ProjectVersionRow[];
  onSwitchVersion?: (versionNumber: number) => Promise<void>;
  // When true, the card mounts with a "✓ Régénéré avec tes réponses"
  // (or context-kind specific) success banner that auto-fades after 4s.
  // Set by the parent right after a regen replaces the card so the user
  // SEES the change happened, not just the (potentially similar-looking)
  // new manifest content.
  regenJustHappened?: VersionContextKind;

  // 2026-05-19 Phase 3.A BYO — Configure backend panel. Lets the user
  // paste their Supabase URL + anon key BEFORE clicking "Push to Forgejo"
  // so the bootstrap repo's `.env.local` is baked with their backend.
  //
  // onSaveBackend is the persistence callback (writes the trio kind='byo-
  // supabase' + url + anon to public.projects via saveProjectBackend).
  // onClearBackend clears the trio back to null (re-bootstrap then emits
  // `.env.example` placeholders). Both optional ; the panel renders only
  // when onSaveBackend is provided.
  //
  // backendKind / backendUrl / backendAnonKey reflect the persisted state
  // — when all-null the panel renders the empty form ; when set it renders
  // a "✓ Backend wired" summary with an Edit / Clear affordance.
  onSaveBackend?: (url: string, anonKey: string) => Promise<void>;
  onClearBackend?: () => Promise<void>;
  backendKind?: 'byo-supabase' | 'genesis-rest' | 'full-supabase' | null;
  backendUrl?: string | null;
  backendAnonKey?: string | null;
  // Genesis-hosted backend. When provided, the empty backend slot shows a primary
  // "Activer la base de données" CTA that opens the 2-step mise-en-ligne wizard
  // (short handle → backend model). On confirm the wizard calls this with the
  // chosen { handle, model }; success flips the slot to the hosted state. Throws
  // map to inline errors inside the wizard.
  // (alreadyProvisioned is part of the bridge contract; the card renders the hosted state either way — callers may use it for messaging.)
  onProvisionBackend?: (args: { handle: string; model: BaseModel }) => Promise<{ backendUrl: string; backendAnonKey: string; expiresAt: string | null; alreadyProvisioned: boolean }>;
  // deploy_frontend (gate on): the wizard becomes "Mettre en ligne mon appli" and
  // confirm calls this — deploy the front (+ optional base when model !== 'none') —
  // returning the deploy outcome so the wizard shows build-progress → live URL.
  onDeployFrontend?: (args: { handle: string; model: BackendModel }) => Promise<DeployOutcome>;
  // Poll the deploy status (building → live | failed) while the progress step shows.
  onPollDeployStatus?: () => Promise<{ status: DeployStatus; deployUrl: string | null; logTail?: string }>;
  // Optional: fired when the wizard's full-supabase tier is gated behind Cloud
  // (post-launch) and the user confirms it — route to the Cloud upsell.
  onCloudUpsell?: () => void;
  // True when there is no Genesis session — the CTA renders disabled with a sign-in hint.
  backendHostSignedOut?: boolean;
  // ISO date string for the genesis-rest trial expiry, shown in the hosted state.
  expiresAt?: string | null;
  // Sync badge (carte unifiée) — when provided, appended into the card head.
  // Computed by the caller from syncStatusFor() + renderSyncBadge().
  headerBadge?: HTMLElement;
}

// Format the magnified result as Markdown the user can paste directly into
// any AI editor (Claude Code, Cursor, Aider…) as the project starter.
// Headings + bullet/ordered lists mirror the on-screen card so the pasted
// content reads identically to what the user just visually validated.
// Stack line is omitted when absent — matches the optional schema field.
export function buildManifestMarkdown(result: MagnifyResult): string {
  const lines: string[] = [];
  lines.push(`# ${result.title}`);
  lines.push('');
  lines.push(`## ${t('magnify.card.outlineHeading')}`);
  for (const item of result.outline) lines.push(`- ${item}`);
  lines.push('');
  lines.push(`## ${t('magnify.card.stepsHeading')}`);
  result.first_steps.forEach((item, idx) => lines.push(`${idx + 1}. ${item}`));
  if (result.stack) {
    lines.push('');
    lines.push(`## ${t('magnify.card.stackHeading')}`);
    lines.push(result.stack);
  }
  return lines.join('\n');
}

// One-line recap for the collapsed fiche artefact (layout B) : the first few
// outline beats joined by " · ", then the suggested stack. Mirrors the
// "Sommaire · … — Stack : …" line in welcome-desktop-B.html. Falls back to a
// generic prompt when the manifest has neither outline nor stack.
function buildFicheSummary(result: MagnifyResult): string {
  const head = (result.outline ?? [])
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 3)
    .join(' · ');
  const stack = result.stack?.trim();
  const stackPart = stack ? `${head ? ' — ' : ''}${t('fiche.stackPrefix')} : ${stack}` : '';
  return `${head}${stackPart}`.trim() || t('fiche.emptySummary');
}

// v7.8.9 — the COLLAPSED résumé for "ton projet". Operator 2026-05-29 :
// "le résumé n'est que le contenu déplié sans mise en forme, pas un résumé".
// buildFicheSummary just concatenates the outline beats = the same content as
// the expanded view. A genuine one-line résumé is the user's ORIGINAL idea
// (their premise, in prose), which reads like a pitch and is distinct from the
// AI's bulleted outline (= the exhaustive description shown when expanded). Fall
// back to the outline·stack recap when no idea is available (legacy call sites).
function buildFicheResume(opts: ProjectPreviewCardOptions): string {
  const idea = opts.idea?.trim();
  return idea || buildFicheSummary(opts.result);
}

export function renderProjectPreviewCard(opts: ProjectPreviewCardOptions): HTMLElement {
  const root = document.createElement('article');
  root.className = 'project-preview-card';
  root.setAttribute('data-component', 'project-preview-card');

  const outlineItems = opts.result.outline.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  const stepItems = opts.result.first_steps.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
  const stackBlock = opts.result.stack
    ? `
      <div class="project-preview-card__section project-preview-card__section--stack">
        <div class="project-preview-card__heading">${escapeHtml(t('magnify.card.stackHeading'))}</div>
        <div class="project-preview-card__stack-text">${escapeHtml(opts.result.stack)}</div>
      </div>`
    : '';

  // Version chip — shown only when version data is wired in (saved
  // projects). Plain v1 = no nav arrows ; vN where N>1 = prev/next.
  const versionChipHtml = (typeof opts.currentVersion === 'number' && opts.versions && opts.versions.length > 0) ? `
    <div class="project-preview-card__version-chip" data-component="version-chip">
      <button type="button" data-component="version-prev" class="project-preview-card__version-nav" aria-label="${escapeHtml(t('version.prevAria'))}" ${opts.currentVersion <= 1 ? 'disabled' : ''}>‹</button>
      <span class="project-preview-card__version-label" title="${escapeHtml(buildVersionTooltip(opts.versions, opts.currentVersion))}">v${opts.currentVersion} / ${opts.versions.length}</span>
      <button type="button" data-component="version-next" class="project-preview-card__version-nav" aria-label="${escapeHtml(t('version.nextAria'))}" ${opts.currentVersion >= opts.versions.length ? 'disabled' : ''}>›</button>
    </div>
  ` : '';

  // Regen-just-happened banner — visible cue that the new version landed.
  // Auto-fades via CSS animation after 4s. Copy adapts to the context_kind
  // (discovery vs enriched vs regen) so the user knows WHY the manifest
  // changed.
  const bannerHtml = opts.regenJustHappened ? `
    <div class="project-preview-card__regen-banner" data-component="regen-banner" data-kind="${escapeHtml(opts.regenJustHappened)}">
      ${escapeHtml(t(`version.bannerKind.${opts.regenJustHappened}`))}
    </div>
  ` : '';

  // "📁 Lier un dossier" proxies the drop-zone folder picker mounted in the
  // attachments block below — only present when the project has an identity
  // (the attachments block, hence the picker, only renders then).
  const linkFolderBtnHtml = (opts.userId && opts.projectId)
    ? `<button type="button" data-component="magnify-link-folder" class="project-preview-card__link-folder">📁 ${escapeHtml(t('dropZone.addFolder'))}</button>`
    : '';
  // "↻ Régénérer enrichi" lives in the actions row (mockup) but is meaningless
  // without attachments, so it starts disabled and is enabled by the
  // attachments onContextChange the moment the first file lands.
  const regenEnrichedBtnHtml = opts.onRegenerateEnriched
    ? `<button type="button" data-component="magnify-regen-enriched" class="project-preview-card__regen-enriched-btn" data-state="idle" disabled>
        <span data-component="magnify-regen-enriched-label">${escapeHtml(t('magnify.card.regenEnriched'))}</span>
      </button>`
    : '';

  root.innerHTML = `
    ${bannerHtml}
    <div class="project-preview-card__card" data-component="ppc-card">
      <div class="project-preview-card__head">
        <span class="project-preview-card__label">${escapeHtml(t('magnify.card.label'))}</span>
      </div>
      <h3 class="project-preview-card__title">${escapeHtml(opts.result.title)}</h3>
      <div class="project-preview-card__section">
        <div class="project-preview-card__heading">${escapeHtml(t('magnify.card.outlineHeading'))}</div>
        <ul class="project-preview-card__list">${outlineItems}</ul>
      </div>
      <div class="project-preview-card__section project-preview-card__section--steps">
        <div class="project-preview-card__heading">${escapeHtml(t('magnify.card.stepsHeading'))}</div>
        <ol class="project-preview-card__list project-preview-card__list--ordered">${stepItems}</ol>
      </div>
      ${stackBlock}
      <div class="project-preview-card__actions">
        <button type="button" data-component="magnify-regenerate" class="project-preview-card__regenerate">${escapeHtml(t('magnify.card.regenerate'))}</button>
        ${regenEnrichedBtnHtml}
        ${opts.onSave ? `
          <button type="button" data-component="magnify-save" class="project-preview-card__save-btn" data-state="${opts.initialSaved ? 'saved' : 'idle'}">
            <span data-component="magnify-save-label">${escapeHtml(t(opts.initialSaved ? 'magnify.card.saved' : 'magnify.card.saveActive'))}</span>
          </button>
        ` : ''}
        <button type="button" data-component="magnify-copy" class="project-preview-card__copy" data-state="idle">
          <span data-component="magnify-copy-label">${escapeHtml(t('magnify.card.copy'))}</span>
        </button>
        ${linkFolderBtnHtml}
        ${versionChipHtml}
      </div>
      ${opts.onRegenerateEnriched ? `<p class="project-preview-card__regen-enriched-hint">${escapeHtml(t('magnify.card.regenEnrichedHint'))}</p>` : ''}
    </div>
  `;

  // Sync badge (carte unifiée Task 5) — appended into the card head when provided.
  // ONLY in the non-collapsible mode here: the collapsible branch below REMOVES
  // `.project-preview-card__head` (the title + label move into the fiche-artefact
  // header), so a badge mounted here would be destroyed. The collapsible branch
  // re-mounts the badge into `.project-fiche-artefact__head` instead.
  if (opts.headerBadge && !opts.collapsible) {
    root.querySelector('.project-preview-card__head')?.appendChild(opts.headerBadge);
  }

  // The result card is an inner bordered box; attachments / discovery / backend
  // append to `root` as separate boxes below it (mockup structure). The ship
  // ("🚀 Préparer mon starter Git") button lives INSIDE this card box, last.
  const cardBox = root.querySelector<HTMLElement>('[data-component="ppc-card"]')!;
  if (opts.onPushForgejo || opts.forgejoRepoUrl) {
    cardBox.appendChild(buildForgejoSlot(opts));
  }

  // Guided first-run beat ⑤ (§5.9): the readiness gauge + « ✦ Construis-le » beacon,
  // a self-contained block-level spine at the foot of the fiche box. Block flow →
  // it stacks, never overlaps (operator guard). Guided runs only; non-guided cards
  // are byte-for-byte unchanged.
  if (opts.guided) {
    cardBox.appendChild(buildGuidedSpine(opts));
  }

  // ── Chemise d'atelier (cover + ledger) — mock zone-projet-v2, validated ──
  // The cover replaces the fiche-artefact head + pinned actions shelf; fiche
  // and plan become uniform ledger <details>. Wiring below (regenerate / copy /
  // save / link-folder / version nav) queries `root`, so the cover is built
  // UNDER root and welcome.ts moves it to the full-width host post-render.
  if (opts.collapsible && opts.cover) {
    const cv = opts.cover;
    const versionChip = cardBox.querySelector<HTMLElement>('[data-component="version-chip"]');

    const cover = document.createElement('div');
    cover.className = 'project-cover';
    cover.setAttribute('data-component', 'project-cover');

    const tab = document.createElement('div');
    tab.className = 'project-cover__tab';
    const opened = cv.createdAt ? new Date(cv.createdAt) : null;
    const dateLocale = cv.locale === 'fr' ? 'fr-FR' : cv.locale === 'en' ? 'en-GB' : undefined;
    const openedTxt = opened && !Number.isNaN(opened.getTime())
      ? ` — ${t('cover.openedOn').replace('{date}', opened.toLocaleDateString(dateLocale, { day: 'numeric', month: 'long' }))}`
      : '';
    tab.innerHTML = `✦ ${escapeHtml(t('cover.tab'))}<span class="project-cover__tab-hand">${escapeHtml(openedTxt)}</span>`;
    cover.appendChild(tab);

    const coverBody = document.createElement('section');
    coverBody.className = 'project-cover__body';
    cover.appendChild(coverBody);

    // Head — serif title with the em tail (mock: accent the last words), the
    // existing version selector chip, the sync badge.
    const head = document.createElement('div');
    head.className = 'project-cover__head';
    const title = document.createElement('h2');
    title.className = 'project-cover__title';
    const words = opts.result.title.trim().split(/\s+/);
    const emCount = words.length > 3 ? 2 : words.length > 1 ? 1 : 0;
    if (emCount) {
      title.innerHTML = `${escapeHtml(words.slice(0, words.length - emCount).join(' '))} <em>${escapeHtml(words.slice(words.length - emCount).join(' '))}</em>`;
    } else {
      title.textContent = opts.result.title;
    }
    head.appendChild(title);
    if (versionChip) head.appendChild(versionChip);
    if (opts.headerBadge) head.appendChild(opts.headerBadge);
    coverBody.appendChild(head);

    // Fil d'état — the journey line. Steps are sequential; the LAST achieved
    // step is the « now » dot, earlier achieved are ✓, the rest are todo.
    const thread = document.createElement('p');
    thread.className = 'project-cover__thread';
    thread.setAttribute('data-component', 'cover-thread');
    const steps: Array<{ label: string; done: boolean; url?: string }> = [
      { label: t('cover.stepIdea'), done: true },
      { label: t('cover.stepBuilt'), done: cv.built },
      { label: t('cover.stepDb'), done: cv.dbActive },
      { label: t('cover.stepOnline'), done: cv.online === 'live', url: cv.onlineUrl },
    ];
    const lastDone = steps.reduce((acc, s, i) => (s.done ? i : acc), 0);
    steps.forEach((s, i) => {
      if (i > 0) {
        const arrow = document.createElement('span');
        arrow.className = 'project-cover__thread-arrow';
        arrow.textContent = '→';
        thread.appendChild(arrow);
      }
      const el = document.createElement('span');
      el.className = `project-cover__step ${s.done ? (i === lastDone ? 'is-now' : 'is-done') : 'is-todo'}`;
      el.setAttribute('data-component', 'cover-step');
      el.textContent = s.label;
      if (s.url) {
        el.append(' ');
        if (s.done) {
          const a = document.createElement('a');
          a.href = `https://${s.url}`;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.textContent = s.url;
          el.appendChild(a);
        } else {
          // Projected address — honest plain text, not a link (nothing live yet).
          const u = document.createElement('span');
          u.className = 'project-cover__step-url';
          u.textContent = s.url;
          el.appendChild(u);
        }
      }
      thread.appendChild(el);
    });
    coverBody.appendChild(thread);

    // Toolbar — the ONE place for actions. The existing wired buttons MOVE here
    // (listeners survive the move; the wiring queries root which still contains
    // the cover at wiring time).
    const toolbar = document.createElement('div');
    toolbar.className = 'project-cover__toolbar';
    toolbar.setAttribute('data-component', 'cover-toolbar');
    const lbl = document.createElement('span');
    lbl.className = 'project-cover__toolbar-lbl';
    lbl.textContent = t('cover.actions');
    toolbar.appendChild(lbl);
    const g1 = document.createElement('span');
    g1.className = 'project-cover__tb-group';
    const g2 = document.createElement('span');
    g2.className = 'project-cover__tb-group';
    const moveBtn = (sel: string, host: HTMLElement, primary = false): void => {
      const b = root.querySelector<HTMLElement>(sel);
      if (!b) return;
      b.classList.add('project-cover__tbtn');
      if (primary) b.classList.add('project-cover__tbtn--primary');
      host.appendChild(b);
    };
    // Mock toolbar = SHORT labels. Set them BEFORE moving (the buttons leave
    // root for detached group spans) and before the handlers wire (the
    // regenerate handler captures its revert label from textContent at wiring
    // time, so the short label round-trips through busy states).
    const shortLabel = (sel: string, label: string): void => {
      const el = root.querySelector<HTMLElement>(sel);
      if (el) el.textContent = label;
    };
    shortLabel('[data-component="magnify-regenerate"]', t('cover.actRegen'));
    shortLabel('[data-component="magnify-regen-enriched-label"]', t('cover.actEnriched'));
    shortLabel('[data-component="magnify-copy-label"]', t('cover.actCopy'));
    shortLabel('[data-component="magnify-link-folder"]', t('cover.actFolder'));
    shortLabel('[data-component="magnify-forgejo-label"]', t('cover.actStarter'));
    moveBtn('[data-component="magnify-regenerate"]', g1, true);
    moveBtn('[data-component="magnify-regen-enriched"]', g1);
    moveBtn('[data-component="magnify-save"]', g2);
    moveBtn('[data-component="magnify-copy"]', g2);
    moveBtn('[data-component="magnify-link-folder"]', g2);
    const forgejoSlot = root.querySelector<HTMLElement>('[data-component="magnify-forgejo-slot"]');
    if (forgejoSlot) {
      forgejoSlot.classList.add('project-cover__tb-forgejo');
      g2.appendChild(forgejoSlot);
    }
    const sep = document.createElement('span');
    sep.className = 'project-cover__tb-sep';
    toolbar.append(g1, sep, g2);
    const backSlot = document.createElement('span');
    backSlot.className = 'project-cover__back-slot';
    backSlot.setAttribute('data-component', 'cover-back-slot');
    toolbar.appendChild(backSlot);
    coverBody.appendChild(toolbar);
    // The regen-enriched hint is NOT in the validated mock — the attachments
    // section carries its own drop-zone guidance. Remove it in cover mode.
    root.querySelector('.project-preview-card__regen-enriched-hint')?.remove();
    // The old actions row is now empty chrome — drop it.
    cardBox.querySelector('.project-preview-card__actions')?.remove();

    root.insertAdjacentElement('afterbegin', cover);

    // ── Ledger section : ✦ Ta fiche (essence + sommaire) ──────────────
    cardBox.querySelector('.project-preview-card__head')?.remove();
    cardBox.querySelector('.project-preview-card__title')?.remove();
    const fiche = document.createElement('details');
    fiche.className = 'ledger-sec';
    fiche.setAttribute('data-component', 'ledger-fiche');
    if (!opts.collapsed) fiche.open = true;
    const fSummary = document.createElement('summary');
    fSummary.className = 'ledger-sec__summary';
    fSummary.innerHTML = `${escapeHtml(t('cover.ficheLabel'))}<span class="ledger-sec__hint">${escapeHtml(t('cover.ficheHint'))}</span>`;
    fiche.appendChild(fSummary);
    const fBody = document.createElement('div');
    fBody.className = 'ledger-sec__body';
    const essence = document.createElement('p');
    essence.className = 'ledger-fiche__essence';
    essence.textContent = buildFicheResume(opts);
    fBody.appendChild(essence);
    const outlineSection = cardBox.querySelector<HTMLElement>(
      '.project-preview-card__section:not(.project-preview-card__section--steps):not(.project-preview-card__section--stack)',
    );
    const outlineList = outlineSection?.querySelector<HTMLElement>('.project-preview-card__list');
    if (outlineList) fBody.appendChild(outlineList);
    outlineSection?.remove();
    fiche.appendChild(fBody);
    cardBox.insertAdjacentElement('beforebegin', fiche);

    // ── Ledger section : 🧭 Plan défini (wraps the remaining card box) ──
    const plan = document.createElement('details');
    plan.className = 'ledger-sec';
    plan.setAttribute('data-component', 'ledger-plan');
    if (!opts.collapsed) plan.open = true;
    const pSummary = document.createElement('summary');
    pSummary.className = 'ledger-sec__summary';
    const planCount = opts.ledgerHints?.planCount;
    const planHint = [
      planCount ? `${planCount} ${t('cover.planHintSteps')}` : '',
      opts.result.stack ?? '',
    ].filter(Boolean).join(' · ');
    pSummary.innerHTML = `${escapeHtml(t('cover.planLabel'))}${
      planCount ? `<span class="ledger-sec__count">${planCount}</span>` : ''
    }${planHint ? `<span class="ledger-sec__hint">${escapeHtml(planHint)}</span>` : ''}`;
    plan.appendChild(pSummary);
    const pBody = document.createElement('div');
    pBody.className = 'ledger-sec__body';
    cardBox.insertAdjacentElement('beforebegin', plan);
    pBody.appendChild(cardBox);
    plan.appendChild(pBody);

    // Keep the labelled sub-vignettes (operator 2026-06-11 : « on avait une
    // vraie vignette ») — the broken rendering was the legacy card stripe
    // (killed in CSS), NOT the headings. Fix only what lied : the steps
    // heading hardcoded « 3 premières étapes » → honest dynamic label.
    const stepsHeading = cardBox.querySelector<HTMLElement>('.project-preview-card__section--steps .project-preview-card__heading');
    if (stepsHeading) {
      stepsHeading.textContent = t('cover.planStepsBare');
    }
  }

  // Layout B (fast-follow) — collapsible "fiche" artefact. The full card box
  // above becomes a deferred surface behind a pinned summary : title + a
  // one-line outline/stack recap + "déplier la fiche complète + actions →".
  // Découverte + chat stay visible siblings below (appended to root later), so
  // collapsing the fiche keeps the conversational surface in reach — matching
  // welcome-desktop-B.html state ②. Opt-in : pre-layout-B call sites skip it.
  if (opts.collapsible && !opts.cover) {
    // v7.8.5 (2026-05-29) — operator brief (final, after frustration) :
    // TWO separate cards in the LEFT column, everything foldable at every level.
    //   ① Card "ton projet" : titre + sélecteur de version + synthèse, déplié
    //      par défaut ; toute la carte pliable via son toggle d'en-tête.
    //   ② Card "le plan du projet" : sa propre carte pliable ; CHAQUE
    //      sous-section (Sommaire · Premières étapes · Stack) pliable seule.
    //   Les actions (↻ régénérer · 📋 copier · 📁 dossier · 🚀 starter Git)
    //   restent visibles sous les deux cartes.

    // The clickable version selector lives in the actions row by default ; lift
    // it into the "ton projet" header so it's the obvious, always-reachable
    // selector (operator : "on ne peut plus sélectionner la version").
    const versionChip = cardBox.querySelector<HTMLElement>('[data-component="version-chip"]');

    // ── Card ① : ton projet ───────────────────────────────────────────
    const projCard = document.createElement('div');
    projCard.className = 'project-fiche-artefact';
    projCard.setAttribute('data-component', 'fiche-artefact');
    projCard.innerHTML = `
      <div class="project-fiche-artefact__head">
        <span class="project-fiche-artefact__label">${escapeHtml(t('fiche.label'))}</span>
        <span class="project-fiche-artefact__title">${escapeHtml(opts.result.title)}</span>
        <span class="project-fiche-artefact__version-slot" data-component="ton-projet-version"></span>
        <button type="button" class="project-fiche-artefact__toggle" data-component="fiche-synthese-toggle" aria-expanded="true">
          <span data-component="fiche-synthese-toggle-label"></span>
        </button>
      </div>
      <div class="project-fiche-artefact__body" data-component="ton-projet-body">
        <p class="project-fiche-artefact__summary" data-component="proj-resume">${escapeHtml(buildFicheResume(opts))}</p>
        <div class="project-fiche-artefact__description" data-component="proj-description"></div>
      </div>
    `;
    cardBox.insertAdjacentElement('beforebegin', projCard);
    if (versionChip) {
      projCard.querySelector<HTMLElement>('[data-component="ton-projet-version"]')!.appendChild(versionChip);
    }
    // Sync badge (carte unifiée) — the collapsible mode is what welcome.ts always
    // uses, and it removed `.project-preview-card__head` above, so mount the badge
    // into the surviving fiche-artefact header (next to title + version selector).
    if (opts.headerBadge) {
      projCard.querySelector<HTMLElement>('.project-fiche-artefact__head')!.appendChild(opts.headerBadge);
    }

    // "ton projet" déplié = description exhaustive = le Sommaire complet (ce que
    // fait l'appli). Lift the outline section's list into the description, then
    // drop the outline from the plan card (plan = premières étapes + stack) so
    // it isn't duplicated. Collapsed shows the résumé one-liner instead.
    const outlineSection = cardBox.querySelector<HTMLElement>(
      '.project-preview-card__section:not(.project-preview-card__section--steps):not(.project-preview-card__section--stack)',
    );
    const outlineList = outlineSection?.querySelector<HTMLElement>('.project-preview-card__list');
    const descHost = projCard.querySelector<HTMLElement>('[data-component="proj-description"]')!;
    if (outlineList) descHost.appendChild(outlineList);
    else descHost.remove();
    outlineSection?.remove();

    // ── Card ② : le plan du projet ─────────────────────────────────────
    // The cardBox (.project-preview-card__card) becomes the plan card. Remove
    // the duplicate "✦ THE PROJECT" head + h3 (title shows in Card ①), add a
    // plan header at the top, and wrap the sub-sections in a foldable body.
    cardBox.querySelector('.project-preview-card__head')?.remove();
    cardBox.querySelector('.project-preview-card__title')?.remove();

    const planHead = document.createElement('div');
    planHead.className = 'project-plan-detaille__head';
    planHead.setAttribute('data-component', 'plan-header');
    planHead.innerHTML = `
      <span class="project-plan-detaille__label">${escapeHtml(t('fiche.planLabel'))}</span>
      <button type="button" class="project-fiche-artefact__toggle project-plan-detaille__toggle" data-component="fiche-toggle" aria-expanded="true">
        <span data-component="fiche-toggle-label"></span>
      </button>
    `;
    cardBox.insertAdjacentElement('afterbegin', planHead);

    const planBody = document.createElement('div');
    planBody.className = 'project-plan-detaille__body';
    planBody.setAttribute('data-component', 'plan-body');
    planHead.insertAdjacentElement('afterend', planBody);
    // Move every sub-section (outline · steps · stack) into the plan body.
    for (const sec of Array.from(cardBox.querySelectorAll<HTMLElement>('.project-preview-card__section'))) {
      planBody.appendChild(sec);
    }

    // Make each sub-section independently foldable : its heading is the toggle.
    for (const sec of Array.from(planBody.querySelectorAll<HTMLElement>('.project-preview-card__section'))) {
      sec.setAttribute('data-component', 'plan-subsection');
      makeSubsectionFoldable(sec);
    }

    // ── Action shelf + starter Git : OUT of the plan card ──
    // Non-guided: pinned after the card, always visible (unchanged path).
    // Guided (incr.4b §6.6, Appendix D.2): the WHOLE shelf defers behind ONE
    // collapsed « ✦ aller plus loin » fold — the beacon stays the single CTA.
    // Same native <details> idiom as the section folds; self-scoped (.gfr)
    // because the prod card mounts with no .gfr ancestor (incr.2 lesson).
    const actionsRow = cardBox.querySelector<HTMLElement>('.project-preview-card__actions');
    const forgejoSlot = cardBox.querySelector<HTMLElement>('[data-component="magnify-forgejo-slot"]');
    const regenHint = cardBox.querySelector<HTMLElement>('.project-preview-card__regen-enriched-hint');
    if (opts.guided) {
      const more = document.createElement('details');
      more.className = 'ppc-more gfr';
      more.setAttribute('data-component', 'ppc-more');
      more.innerHTML = `
        <summary class="ppc-more__summary"><span class="ppc-more__chev" aria-hidden="true">▸</span> ✦ ${escapeHtml(t('onboarding.allerPlusLoin'))}</summary>
        <div class="ppc-more__body" data-component="ppc-more-body"></div>
      `;
      const moreBody = more.querySelector<HTMLElement>('[data-component="ppc-more-body"]')!;
      if (actionsRow) { actionsRow.classList.add('project-preview-card__actions--pinned'); moreBody.appendChild(actionsRow); }
      if (regenHint) moreBody.appendChild(regenHint);
      if (forgejoSlot) moreBody.appendChild(forgejoSlot);
      cardBox.insertAdjacentElement('afterend', more);
    } else {
      let shelfAnchor: Element = cardBox;
      if (actionsRow) {
        actionsRow.classList.add('project-preview-card__actions--pinned');
        shelfAnchor.insertAdjacentElement('afterend', actionsRow);
        shelfAnchor = actionsRow;
      }
      if (regenHint) { shelfAnchor.insertAdjacentElement('afterend', regenHint); shelfAnchor = regenHint; }
      if (forgejoSlot) { shelfAnchor.insertAdjacentElement('afterend', forgejoSlot); }
    }

    // ── Wire Card ① fold — swaps résumé (collapsed) ↔ description (expanded) ──
    // Operator brief : "titre + résumé en replié, description exhaustive en
    // déplié". The header (title + version selector) stays put ; only the body
    // content swaps between the one-line résumé and the full Sommaire.
    const sToggle = projCard.querySelector<HTMLButtonElement>('[data-component="fiche-synthese-toggle"]')!;
    const sToggleLabel = projCard.querySelector<HTMLElement>('[data-component="fiche-synthese-toggle-label"]')!;
    const projResume = projCard.querySelector<HTMLElement>('[data-component="proj-resume"]')!;
    const projDesc = projCard.querySelector<HTMLElement>('[data-component="proj-description"]');
    const hasDesc = !!projDesc && !!outlineList;
    let projOpen = true;
    const setProjOpen = (o: boolean): void => {
      projOpen = o;
      // collapsed → résumé court ; expanded → description exhaustive (Sommaire).
      projResume.hidden = o && hasDesc;
      if (projDesc) projDesc.hidden = !o;
      projCard.setAttribute('data-expanded', String(o));
      sToggle.setAttribute('aria-expanded', String(o));
      sToggleLabel.textContent = o ? t('fiche.collapse') : t('fiche.expand');
    };
    setProjOpen(true); // default expanded → exhaustive description visible
    sToggle.addEventListener('click', () => setProjOpen(!projOpen));

    // ── Wire Card ② fold (plan body — keeps the plan card header visible) ──
    const pToggle = planHead.querySelector<HTMLButtonElement>('[data-component="fiche-toggle"]')!;
    const pToggleLabel = planHead.querySelector<HTMLElement>('[data-component="fiche-toggle-label"]')!;
    let planOpen = true;
    const setPlanOpen = (o: boolean): void => {
      planOpen = o;
      planBody.hidden = !o;
      planHead.setAttribute('data-expanded', String(o));
      pToggle.setAttribute('aria-expanded', String(o));
      pToggleLabel.textContent = o ? t('fiche.collapse') : t('fiche.expand');
    };
    setPlanOpen(true);
    pToggle.addEventListener('click', () => setPlanOpen(!planOpen));

    // collapsed:true — auto-collapse both fiche synthèse + plan body on mount
    // (desktop delivered state : the agent/build panel needs room). Reuses the
    // same toggle state functions so the re-open path is identical to a user click.
    if (opts.collapsed) {
      setProjOpen(false);
      setPlanOpen(false);
    }
  }

  // Version nav handlers — only wired when chip was rendered.
  if (opts.onSwitchVersion && typeof opts.currentVersion === 'number' && opts.versions) {
    const prevBtn = root.querySelector<HTMLButtonElement>('[data-component="version-prev"]');
    const nextBtn = root.querySelector<HTMLButtonElement>('[data-component="version-next"]');
    const target = opts.currentVersion;
    prevBtn?.addEventListener('click', () => {
      if (target > 1) void opts.onSwitchVersion!(target - 1);
    });
    nextBtn?.addEventListener('click', () => {
      if (target < opts.versions!.length) void opts.onSwitchVersion!(target + 1);
    });
  }

  // Banner auto-fade — give the CSS animation a chance, then remove the
  // node so screen-readers don't keep announcing it. 4s visible + 600ms
  // fade-out matches the CSS keyframes in project-attachments.css.
  if (opts.regenJustHappened) {
    setTimeout(() => {
      const banner = root.querySelector<HTMLElement>('[data-component="regen-banner"]');
      if (banner) banner.style.opacity = '0';
      setTimeout(() => banner?.remove(), 600);
    }, 4_000);
  }

  if (opts.onRegenerate) {
    const regen = root.querySelector<HTMLButtonElement>('[data-component="magnify-regenerate"]')!;
    const regenLabel = regen.textContent ?? '';
    regen.addEventListener('click', async () => {
      if (regen.getAttribute('aria-busy') === 'true') return;
      regen.setAttribute('aria-busy', 'true');
      regen.textContent = t('magnify.card.regenerating');
      try {
        await opts.onRegenerate!();
        // Parent typically replaces the card on success — if it kept us
        // mounted (e.g. sync onRegenerate), revert state so the button
        // is clickable again.
        regen.removeAttribute('aria-busy');
        regen.textContent = regenLabel;
      } catch {
        regen.removeAttribute('aria-busy');
        regen.textContent = regenLabel;
      }
    });
  }

  // Save handler — fires only when onSave was provided (typically the case
  // for a freshly-generated card ; absent when re-opening from "Mes projets").
  // State flow : idle → saving (spinner-like label) → saved (✓, sticky) → user
  // can't re-save the same card. Error → flash error label → revert to idle so
  // the user can retry. With opts.initialSaved=true the button starts at saved.
  if (opts.onSave) {
    const saveBtn = root.querySelector<HTMLButtonElement>('[data-component="magnify-save"]')!;
    const saveLabel = root.querySelector<HTMLElement>('[data-component="magnify-save-label"]')!;
    saveBtn.addEventListener('click', async () => {
      if (saveBtn.getAttribute('data-state') !== 'idle') return; // de-dupe rapid clicks
      saveBtn.setAttribute('data-state', 'saving');
      saveLabel.textContent = t('magnify.card.saving');
      try {
        await opts.onSave!();
        saveBtn.setAttribute('data-state', 'saved');
        saveLabel.textContent = t('magnify.card.saved');
      } catch {
        saveBtn.setAttribute('data-state', 'error');
        saveLabel.textContent = t('magnify.card.saveError');
        setTimeout(() => {
          saveBtn.setAttribute('data-state', 'idle');
          saveLabel.textContent = t('magnify.card.saveActive');
        }, 2400);
      }
    });
  }

  // Attachments — append the drop-zone + list section when we have a
  // persistent identity for the project. Reopening from "Mes projets" passes
  // both (the saved row's id + user_id), fresh Magnify passes them after
  // auto-save resolves. The "Régénérer enrichi" button below tracks whether
  // there's any uploaded context to feed into the next Magnify call.
  // Regen-enriched button (rendered in the actions row above when wired). It
  // starts disabled; the attachments onContextChange enables it on first
  // upload. Wire its busy/error click handler here.
  const regenEnrichedBtn = root.querySelector<HTMLButtonElement>('[data-component="magnify-regen-enriched"]');
  if (regenEnrichedBtn && opts.onRegenerateEnriched) {
    const regenEnrichedLabel = regenEnrichedBtn.querySelector<HTMLElement>('[data-component="magnify-regen-enriched-label"]')!;
    const regenEnrichedIdle = regenEnrichedLabel.textContent ?? t('magnify.card.regenEnriched');
    regenEnrichedBtn.addEventListener('click', async () => {
      if (regenEnrichedBtn.getAttribute('data-state') !== 'idle') return;
      regenEnrichedBtn.setAttribute('data-state', 'busy');
      regenEnrichedLabel.textContent = t('magnify.card.regenEnrichedBusy');
      try {
        await opts.onRegenerateEnriched!();
        // Parent replaces the whole card on success ; if kept mounted (sync
        // path), fall back to idle.
        regenEnrichedBtn.setAttribute('data-state', 'idle');
        regenEnrichedLabel.textContent = regenEnrichedIdle;
      } catch {
        regenEnrichedBtn.setAttribute('data-state', 'error');
        regenEnrichedLabel.textContent = t('magnify.card.regenEnrichedError');
        setTimeout(() => {
          regenEnrichedBtn.setAttribute('data-state', 'idle');
          regenEnrichedLabel.textContent = regenEnrichedIdle;
        }, 2400);
      }
    });
  }

  // Attachments — append the drop-zone + list section when we have a
  // persistent identity for the project. The "Lier un dossier" + "Régénérer
  // enrichi" actions in the card above operate on whatever lands here.
  // Layout B: context/secondary blocks (files + backend) go to the rail when a
  // railTarget is given ; discovery + the fiche/ship stay in the card body.
  const railOrRoot = opts.railTarget ?? root;
  // 2.7.1 item B — a delivered desktop project (collapsible + collapsed) opens
  // with EVERYTHING folded: the fiche + plan above AND the files + discovery
  // sections here. Those two blocks do NOT live in the card box — attachments
  // go straight to railTarget (the caller's right col) and welcome.ts relocates
  // the discovery node out of the card — so the fold is a native <details>
  // WRAPPER that travels with the node (same pattern as the backend slot's
  // details). Folded on mount; the summary header re-opens each section
  // independently. collapsed:false (web) → no wrapper at all, the pre-existing
  // structure is byte-identical.
  const foldSections = !!(opts.collapsible && opts.collapsed);
  // Chemise mode : EVERY section gets the uniform ledger chrome (wrapper always
  // present, OPEN unless the desktop-delivered collapse applies). Legacy mode :
  // wrapper only when folding (web stays byte-identical).
  const ledgerChrome = !!(opts.collapsible && opts.cover);
  const wrapSectionFold = (
    label: string,
    kind: 'attachments' | 'discovery' | 'backend',
    node: HTMLElement,
    extra?: { hint?: string; count?: number },
  ): HTMLElement => {
    const details = document.createElement('details');
    details.className = `project-preview-card__section-fold${ledgerChrome ? ' ledger-sec' : ''}`;
    details.setAttribute('data-component', 'ppc-section-fold');
    details.setAttribute('data-fold', kind);
    if (!foldSections) details.open = true;
    const summary = document.createElement('summary');
    summary.className = `project-preview-card__section-fold-summary${ledgerChrome ? ' ledger-sec__summary' : ''}`;
    summary.textContent = label;
    if (ledgerChrome && extra?.count != null && extra.count > 0) {
      const c = document.createElement('span');
      c.className = 'ledger-sec__count';
      c.textContent = String(extra.count);
      summary.appendChild(c);
    }
    if (ledgerChrome && extra?.hint) {
      const hint = document.createElement('span');
      hint.className = 'ledger-sec__hint';
      hint.textContent = extra.hint;
      summary.appendChild(hint);
    }
    details.appendChild(summary);
    details.appendChild(node);
    // Mirror the inner node's hidden state (the fresh discovery CTA slot starts
    // hidden until first upload) — the onContextChange reveal unhides both.
    details.hidden = node.hidden;
    return details;
  };
  const sectionOrBare = (
    label: string,
    kind: 'attachments' | 'discovery' | 'backend',
    node: HTMLElement,
    extra?: { hint?: string; count?: number },
  ): HTMLElement => (foldSections || ledgerChrome ? wrapSectionFold(label, kind, node, extra) : node);
  let discoverySlot: HTMLElement | null = null;
  if (opts.userId && opts.projectId) {
    const attachmentsEl = renderProjectAttachments({
      userId: opts.userId,
      projectId: opts.projectId,
      onContextChange: () => {
        // First attachment → reveal the discovery CTA + enable regen-enriched.
        if (discoverySlot) {
          discoverySlot.hidden = false;
          // Folded mode: the CTA slot travels inside a details wrapper that
          // mirrors its hidden state — reveal the wrapper too.
          discoverySlot.closest<HTMLElement>('[data-component="ppc-section-fold"]')?.removeAttribute('hidden');
        }
        if (regenEnrichedBtn) regenEnrichedBtn.disabled = false;
      },
    });
    railOrRoot.appendChild(
      sectionOrBare(
        ledgerChrome ? t('cover.attachLabel') : t('attachments.heading'),
        'attachments', attachmentsEl, { hint: t('cover.attachHint') },
      ),
    );

    // v26 — RESTORE path : if the project already has stored discovery,
    // render the discovery-panel directly from mount with prior answers
    // pre-filled. Skip the "🔍 Genesis te pose des questions" CTA and
    // skip the revealedOnUpload gate. The user lands on the panel
    // already populated and can edit + resubmit.
    //
    // FRESH path (else branch) : standard CTA hidden until first upload.
    if (opts.storedDiscovery && opts.onFinishDiscovery) {
      const restoredPanel = opts.guided
        ? foldedDiscoverySurface(opts, opts.storedDiscovery, opts.storedAnswers)
        : renderDiscoveryPanel({
            discovery: opts.storedDiscovery,
            initialAnswers: opts.storedAnswers,
            onSubmit: async (answers) => {
              await opts.onFinishDiscovery!(answers, opts.storedDiscovery!);
            },
            onSaveDraft: opts.onSaveDraft,
            onRerun: opts.onRestartDiscovery,
          });
      root.appendChild(
        sectionOrBare(
          ledgerChrome ? t('cover.discoveryLabel') : t('discovery.chip'),
          'discovery', restoredPanel,
          { hint: t('cover.discoveryHint'), count: opts.ledgerHints?.discoveryCount },
        ),
      );
    } else if (opts.onStartDiscovery) {
      discoverySlot = buildDiscoverySlot(opts);
      root.appendChild(
        sectionOrBare(
          ledgerChrome ? t('cover.discoveryLabel') : t('discovery.chip'),
          'discovery', discoverySlot,
        ),
      );
    }

    // 2026-05-19 Phase 3.A BYO — backend config panel. Sits between
    // discovery (refines the manifest) and Forgejo push (ships the repo)
    // because the user needs to wire backend BEFORE pushing — the values
    // get baked into the bootstrapped `.env.local`. Skipping the panel
    // is fine ; bootstrap then emits `.env.example` placeholders.
    if (opts.onSaveBackend) {
      // Rail order (layout B): backend is the LAST rail block (after online +
      // upsell, which the caller appends post-build). It lands in its own
      // pre-positioned slot when given ; otherwise falls back to the files rail.
      // Chemise mode : the mock places 🗄 Base de données in the LEFT column
      // (under the plan) — the card root. Legacy keeps the rail fallback.
      const backendHost = opts.backendTarget ?? (ledgerChrome ? root : railOrRoot);
      const backendSlot = buildBackendSlot(opts);
      backendHost.appendChild(
        ledgerChrome
          ? wrapSectionFold(t('cover.dbLabel'), 'backend', backendSlot, {
              hint: opts.backendKind ? t('cover.dbActive') : t('cover.dbTodo'),
            })
          : backendSlot,
      );
    }
  }

  // "📁 Lier un dossier" in the actions row proxies a click to the drop-zone's
  // folder picker (mounted in the attachments block above), reusing its entire
  // addFiles pipeline — no duplicate wiring. Only present when attachments are.
  root.querySelector<HTMLButtonElement>('[data-component="magnify-link-folder"]')
    ?.addEventListener('click', () => {
      root.querySelector<HTMLButtonElement>('[data-component="drop-zone-folder-btn"]')?.click();
    });

  // (2026-05-17 v17) — download zip button removed. The use case it covered
  // (get the manifest as a local zip to start coding) is now served better
  // by "Préparer mon starter Git" (creates the Forgejo repo directly with
  // README/.claude/CLAUDE.md/.gitignore, ready to `git clone`). The zip
  // path was an anti-pattern for free-tier retention per v14 dette. The
  // zip builder src/projects/zip.ts is kept in the codebase in case a
  // Premium tier brings back the download as a payable feature.

  // Copy manifest to clipboard. Clipboard API requires a secure context
  // (HTTPS — prod is HTTPS so happy path always works there ; localhost
  // also counts as secure for Vite dev). If neither holds (file://, some
  // embedded browser), fall back to the legacy textarea+execCommand path
  // so the user never gets a silent no-op.
  const copyBtn = root.querySelector<HTMLButtonElement>('[data-component="magnify-copy"]')!;
  const copyLabel = root.querySelector<HTMLElement>('[data-component="magnify-copy-label"]')!;
  const copyIdleLabel = copyLabel.textContent ?? t('magnify.card.copy');
  copyBtn.addEventListener('click', async () => {
    const md = buildManifestMarkdown(opts.result);
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(md);
        ok = true;
      } else {
        ok = legacyCopy(md);
      }
    } catch {
      ok = legacyCopy(md);
    }
    copyBtn.setAttribute('data-state', ok ? 'copied' : 'error');
    copyLabel.textContent = t(ok ? 'magnify.card.copied' : 'magnify.card.copyError');
    setTimeout(() => {
      copyBtn.setAttribute('data-state', 'idle');
      copyLabel.textContent = copyIdleLabel; // captured at wiring — keeps the cover's short label
    }, 2200);
    // Open the next-steps modal on successful copy so the user has a clear
    // "what now ?" path instead of bouncing out of Genesis. Empirically caught
    // 2026-05-16 aurum.ai dogfood session : after Copier, user hit a dead
    // end with only Regénérer / re-copy as actions ; modal closes the loop.
    if (ok) {
      document.body.appendChild(renderNextStepsModal());
    }
  });

  return root;
}

// Build the Discovery CTA slot — the "Genesis te pose des questions"
// button. Initially hidden (no attachments) ; the parent's
// onContextChange (fired by project-attachments after every successful
// upload) toggles it visible. On click, calls onStartDiscovery → swaps
// the slot inline with renderDiscoveryPanel. On panel submit → calls
// onFinishDiscovery (which typically re-magnifies + replaces the card).
function buildDiscoverySlot(opts: ProjectPreviewCardOptions): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'project-preview-card__discovery-slot';
  wrap.setAttribute('data-component', 'discovery-slot');
  wrap.hidden = true; // shown by parent on first upload via discoverySlotEl.hidden=false

  wrap.innerHTML = `
    <button type="button" data-component="discovery-cta" class="project-preview-card__discovery-cta" data-state="idle">
      <span data-component="discovery-cta-label">${escapeHtml(t('discovery.cta'))}</span>
    </button>
    <span class="project-preview-card__discovery-hint">${escapeHtml(t('discovery.ctaHint'))}</span>
  `;

  const cta = wrap.querySelector<HTMLButtonElement>('[data-component="discovery-cta"]')!;
  const label = wrap.querySelector<HTMLElement>('[data-component="discovery-cta-label"]')!;
  cta.addEventListener('click', async () => {
    if (cta.getAttribute('data-state') !== 'idle') return;
    cta.setAttribute('data-state', 'busy');
    cta.disabled = true;
    label.textContent = t('discovery.ctaBusy');
    try {
      const discovery = await opts.onStartDiscovery!();
      // Swap the CTA wrap with the discovery surface inline. Guided first-run →
      // the folded conversational surface; otherwise the textarea panel (unchanged).
      const panel = opts.guided && opts.onFinishDiscovery
        ? foldedDiscoverySurface(opts, discovery)
        : renderDiscoveryPanel({
            discovery,
            onSubmit: async (answers) => {
              if (opts.onFinishDiscovery) {
                await opts.onFinishDiscovery(answers, discovery);
              }
            },
            onSaveDraft: opts.onSaveDraft,
            onCancel: () => {
              // Restore the CTA so the user can re-trigger discovery later
              // (e.g. after uploading more files).
              panel.replaceWith(wrap);
              cta.setAttribute('data-state', 'idle');
              cta.disabled = false;
              label.textContent = t('discovery.cta');
            },
          });
      wrap.replaceWith(panel);
    } catch (err) {
      const status = (err as { status?: number }).status;
      console.warn('[discovery] start failed', err, 'status=', status);
      cta.setAttribute('data-state', 'error');
      label.textContent = status === 400
        ? t('discovery.ctaEmpty')
        : status === 422
        ? t('discovery.ctaUnsupported')
        : t('discovery.ctaError');
      setTimeout(() => {
        cta.setAttribute('data-state', 'idle');
        cta.disabled = false;
        label.textContent = t('discovery.cta');
      }, 3500);
    }
  });
  return wrap;
}

// Build the Forgejo CTA slot. Two visual modes :
// - Already-pushed (URL known) : renders as an `<a>` with "✓ On Forgejo"
//   so the user can re-open the repo in one click.
// - Idle (onPushForgejo set, no URL yet) : renders as a `<button>` that
//   calls the bootstrap, swaps itself to the link mode on success, flashes
//   an error then reverts on failure.
// 2026-05-19 Phase 3.A BYO — Configure backend panel. Two visual modes :
// - Wired (backendKind set on the row) : renders a "✓ Backend connecté"
//   summary + a collapsible body with the URL + masked anon key + a
//   "Modifier" affordance that swaps to the form.
// - Empty (no backendKind) : renders a collapsed details with the form
//   (URL input + anon key input + save button), default-collapsed so it
//   doesn't shout at users who'd rather skip and wire it manually later.
//
// Light client-side validation : URL must be http(s) + host shape ; anon
// key must look JWT-shaped (3 base64url segments ≥ 100 chars total). The
// DB CHECK constraints are the real gate ; client validation is just for
// fast UX feedback. The submit handler shows a per-field error message
// inline + flips data-state="error" on the relevant input.
function buildBackendSlot(opts: ProjectPreviewCardOptions): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'project-preview-card__backend';
  wrap.setAttribute('data-component', 'project-backend-slot');

  // Render appropriate state based on whether backend is already wired.
  // Both Genesis-hosted kinds (genesis-rest + full-supabase) render the managed
  // "hosted" summary — the difference (DB-only vs accounts+files) was a one-time
  // choice in the wizard, not an ongoing edit affordance.
  if ((opts.backendKind === 'genesis-rest' || opts.backendKind === 'full-supabase') && opts.backendUrl) {
    renderBackendHosted(wrap, opts);
  } else if (opts.backendKind === 'byo-supabase' && opts.backendUrl && opts.backendAnonKey) {
    renderBackendWired(wrap, opts);
  } else {
    renderBackendEmpty(wrap, opts);
  }
  return wrap;
}

// Genesis-hosted state — collapsed summary (no form, no edit — it's managed).
// Shows the badge, the host, the hint, and (if set) the trial expiry date.
function renderBackendHosted(wrap: HTMLElement, opts: ProjectPreviewCardOptions): void {
  const url = opts.backendUrl ?? '';
  let host = '';
  try { host = new URL(url).host; } catch { host = url; }
  const expiresAt = opts.expiresAt ?? null;
  let expireLine = '';
  if (expiresAt) {
    try {
      const formatted = new Date(expiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
      expireLine = ` · ${escapeHtml(t('backend.genesisHostedExpiry').replace('{date}', formatted))}`;
    } catch { /* ignore */ }
  }
  wrap.innerHTML = `
    <details class="project-preview-card__backend-details" data-state="genesis-hosted">
      <summary class="project-preview-card__backend-summary">
        <span class="project-preview-card__backend-badge project-preview-card__backend-badge--genesis">🗄 ${escapeHtml(t('backend.genesisHostedBadge'))}</span>
        <span class="project-preview-card__backend-host">${escapeHtml(host)}</span>${expireLine ? `<span class="project-preview-card__backend-expiry">${expireLine}</span>` : ''}
      </summary>
      <div class="project-preview-card__backend-body">
        <p class="project-preview-card__backend-hint">${escapeHtml(t('backend.genesisHostedHint'))}</p>
      </div>
    </details>
  `;
}

// Wired state — collapsed summary with edit affordance.
function renderBackendWired(wrap: HTMLElement, opts: ProjectPreviewCardOptions): void {
  const url = opts.backendUrl ?? '';
  const key = opts.backendAnonKey ?? '';
  // Mask anon key : first 12 chars + "…" + last 4. Anon is "public-by-design"
  // but we still don't want to write it in full on screen for visual hygiene
  // (it shows up in screenshots / screen-shares the user does of the card).
  const keyMasked = key.length > 20 ? `${key.slice(0, 12)}…${key.slice(-4)}` : '…';
  wrap.innerHTML = `
    <details class="project-preview-card__backend-details" data-state="wired">
      <summary class="project-preview-card__backend-summary">
        <span class="project-preview-card__backend-badge">✓ ${escapeHtml(t('backend.wiredBadge'))}</span>
        <span class="project-preview-card__backend-host">${escapeHtml(new URL(url).host)}</span>
      </summary>
      <div class="project-preview-card__backend-body">
        <p class="project-preview-card__backend-summary-line">
          <strong>URL :</strong> <code>${escapeHtml(url)}</code>
        </p>
        <p class="project-preview-card__backend-summary-line">
          <strong>${escapeHtml(t('backend.anonKeyLabel'))} :</strong> <code>${escapeHtml(keyMasked)}</code>
        </p>
        <p class="project-preview-card__backend-hint">${escapeHtml(t('backend.wiredHint'))}</p>
        <div class="project-preview-card__backend-actions">
          <button type="button" data-component="backend-edit" class="project-preview-card__backend-edit-btn">${escapeHtml(t('backend.editBtn'))}</button>
          ${opts.onClearBackend ? `<button type="button" data-component="backend-clear" class="project-preview-card__backend-clear-btn">${escapeHtml(t('backend.clearBtn'))}</button>` : ''}
        </div>
      </div>
    </details>
  `;
  wireWiredHandlers(wrap, opts);
}

function wireWiredHandlers(wrap: HTMLElement, opts: ProjectPreviewCardOptions): void {
  wrap.querySelector<HTMLButtonElement>('[data-component="backend-edit"]')?.addEventListener('click', () => {
    renderBackendEmpty(wrap, opts, { prefillUrl: opts.backendUrl ?? '', prefillKey: opts.backendAnonKey ?? '' });
  });
  if (opts.onClearBackend) {
    wrap.querySelector<HTMLButtonElement>('[data-component="backend-clear"]')?.addEventListener('click', async () => {
      const btn = wrap.querySelector<HTMLButtonElement>('[data-component="backend-clear"]')!;
      btn.disabled = true;
      btn.textContent = t('backend.clearBusy');
      try {
        await opts.onClearBackend!();
        renderBackendEmpty(wrap, { ...opts, backendKind: null, backendUrl: null, backendAnonKey: null });
      } catch {
        btn.textContent = t('backend.clearError');
        setTimeout(() => {
          btn.disabled = false;
          btn.textContent = t('backend.clearBtn');
        }, 2400);
      }
    });
  }
}

// Empty / edit state — form with URL + anon key inputs + save button.
// detailOpen on first mount is closed by default to avoid shouting at
// users who'd rather skip ; the user clicks "🗄 Configure backend" to
// open the form. When invoked from "Modifier" (wired → edit), the form
// is pre-opened + pre-filled.
function renderBackendEmpty(
  wrap: HTMLElement,
  opts: ProjectPreviewCardOptions,
  prefill: { prefillUrl: string; prefillKey: string } = { prefillUrl: '', prefillKey: '' },
): void {
  const isEdit = prefill.prefillUrl !== '' || prefill.prefillKey !== '';
  // Build the Genesis-host CTA block (only when onProvisionBackend is wired).
  const hostCtaHtml = opts.onProvisionBackend ? `
    <div class="project-preview-card__backend-provision" data-component="backend-provision">
      <button type="button"
        class="project-preview-card__backend-provision-btn"
        data-component="backend-provision-btn"
        ${opts.backendHostSignedOut ? 'disabled' : ''}>
        ${escapeHtml(t('backend.genesisHostCta'))}
      </button>
      <p class="project-preview-card__backend-provision-hint">
        ${opts.backendHostSignedOut
          ? escapeHtml(t('backend.genesisHostSignedOut'))
          : escapeHtml(t('backend.genesisHostHint'))}
      </p>
    </div>
    <div class="project-preview-card__backend-provision-divider" aria-hidden="true"></div>
  ` : '';
  // The Genesis-host CTA (when wired) renders ABOVE the collapsible BYO form so
  // it's a discoverable 1-click — the host path is primary, BYO is the alternative
  // tucked inside the (collapsed) details. The divider inside hostCtaHtml separates
  // the two. When onProvisionBackend is absent the slot is just the BYO details.
  wrap.innerHTML = `
    ${hostCtaHtml}
    <details class="project-preview-card__backend-details" data-state="empty" ${isEdit ? 'open' : ''}>
      <summary class="project-preview-card__backend-summary">
        <span class="project-preview-card__backend-badge project-preview-card__backend-badge--empty">🗄 ${escapeHtml(t('backend.reframeTitle'))}</span>
      </summary>
      <div class="project-preview-card__backend-body">
        <p class="project-preview-card__backend-intro">${t('backend.intro').replace(
          '{link}',
          `<a href="https://supabase.com/dashboard/projects" target="_blank" rel="noopener noreferrer" class="project-preview-card__backend-link">${escapeHtml(t('backend.introLink'))}</a>`,
        )}</p>
        <label class="project-preview-card__backend-field">
          <span class="project-preview-card__backend-field-label">${escapeHtml(t('backend.urlLabel'))}</span>
          <input type="url" data-component="backend-url-input" class="project-preview-card__backend-input" placeholder="https://abc123.supabase.co" value="${escapeHtml(prefill.prefillUrl)}" autocomplete="off" spellcheck="false">
          <small class="project-preview-card__backend-field-error" data-component="backend-url-error" hidden></small>
        </label>
        <label class="project-preview-card__backend-field">
          <span class="project-preview-card__backend-field-label">${escapeHtml(t('backend.anonKeyLabel'))}</span>
          <input type="text" data-component="backend-key-input" class="project-preview-card__backend-input project-preview-card__backend-input--key" placeholder="eyJhbGciOiJI..." value="${escapeHtml(prefill.prefillKey)}" autocomplete="off" spellcheck="false">
          <small class="project-preview-card__backend-field-error" data-component="backend-key-error" hidden></small>
        </label>
        <div class="project-preview-card__backend-actions">
          <button type="button" data-component="backend-save" class="project-preview-card__backend-save-btn" data-state="idle">
            <span data-component="backend-save-label">${escapeHtml(t('backend.saveBtn'))}</span>
          </button>
        </div>
        <p class="project-preview-card__backend-footnote">${escapeHtml(t('backend.footnote'))}</p>
      </div>
    </details>
  `;
  wireEmptyHandlers(wrap, opts);
}

function wireEmptyHandlers(wrap: HTMLElement, opts: ProjectPreviewCardOptions): void {
  // Wire the Genesis-host provision CTA if present. The button opens the 2-step
  // mise-en-ligne wizard (short handle → backend model); the wizard owns its own
  // busy/error state and calls onProvisionBackend({ handle, model }) on confirm.
  // On success the wizard closes itself and we flip the slot to the hosted state.
  if (opts.onProvisionBackend) {
    const provBtn = wrap.querySelector<HTMLButtonElement>('[data-component="backend-provision-btn"]');
    if (provBtn) {
      provBtn.addEventListener('click', () => {
        if (provBtn.disabled) return;
        const wizard = renderMiseEnLigneWizard({
          title: opts.result.title,
          onConfirm: async ({ handle, model }) => {
            // Throws propagate to the wizard's inline error so the user can retry.
            // deploy_frontend (gate on): deploy the site (+ optional base) and return
            // the outcome so the wizard shows build-progress → live URL.
            if (deployFrontendEnabled() && opts.onDeployFrontend) {
              return await opts.onDeployFrontend({ handle, model });
            }
            // shipped-A: provision the base only, flip the slot, the wizard closes.
            const baseModel = model as BaseModel; // gate off → never 'none'
            const res = await opts.onProvisionBackend!({ handle, model: baseModel });
            renderBackendHosted(wrap, {
              ...opts,
              backendKind: baseModel,
              backendUrl: res.backendUrl,
              backendAnonKey: res.backendAnonKey,
              expiresAt: res.expiresAt,
            });
          },
          onPollStatus: opts.onPollDeployStatus,
          onCloudUpsell: opts.onCloudUpsell,
        });
        document.body.appendChild(wizard);
      });
    }
  }

  if (!opts.onSaveBackend) return;
  const urlInput = wrap.querySelector<HTMLInputElement>('[data-component="backend-url-input"]')!;
  const keyInput = wrap.querySelector<HTMLInputElement>('[data-component="backend-key-input"]')!;
  const urlError = wrap.querySelector<HTMLElement>('[data-component="backend-url-error"]')!;
  const keyError = wrap.querySelector<HTMLElement>('[data-component="backend-key-error"]')!;
  const saveBtn = wrap.querySelector<HTMLButtonElement>('[data-component="backend-save"]')!;
  const saveLabel = wrap.querySelector<HTMLElement>('[data-component="backend-save-label"]')!;

  // Client-side validation mirrors the DB CHECK constraints in
  // 2026-05-19T0300_add_backend_config.sql — same regex shapes. Server
  // is the source of truth ; client validation is just for fast UX.
  const validateUrl = (v: string): string | null => {
    if (!v) return t('backend.errorUrlRequired');
    if (!/^https?:\/\/[a-zA-Z0-9.-]+(:[0-9]+)?(\/.*)?$/.test(v)) return t('backend.errorUrlShape');
    return null;
  };
  const validateKey = (v: string): string | null => {
    if (!v) return t('backend.errorKeyRequired');
    if (v.length < 100) return t('backend.errorKeyTooShort');
    if (!/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(v)) return t('backend.errorKeyShape');
    return null;
  };
  const showErr = (el: HTMLElement, msg: string | null): void => {
    if (msg) { el.textContent = msg; el.hidden = false; }
    else { el.textContent = ''; el.hidden = true; }
  };

  saveBtn.addEventListener('click', async () => {
    if (saveBtn.getAttribute('data-state') !== 'idle') return;
    const url = urlInput.value.trim();
    const key = keyInput.value.trim();
    const eUrl = validateUrl(url);
    const eKey = validateKey(key);
    showErr(urlError, eUrl);
    showErr(keyError, eKey);
    if (eUrl || eKey) return;

    saveBtn.setAttribute('data-state', 'busy');
    saveLabel.textContent = t('backend.saveBusy');
    try {
      await opts.onSaveBackend!(url, key);
      // Re-render in wired state with the saved values.
      renderBackendWired(wrap, { ...opts, backendKind: 'byo-supabase', backendUrl: url, backendAnonKey: key });
    } catch (err) {
      console.warn('[backend] save failed', err);
      saveBtn.setAttribute('data-state', 'error');
      saveLabel.textContent = t('backend.saveError');
      setTimeout(() => {
        saveBtn.setAttribute('data-state', 'idle');
        saveLabel.textContent = t('backend.saveBtn');
      }, 2400);
    }
  });
}

// Make a plan sub-section (Sommaire · Premières étapes · Stack) independently
// foldable via its heading — shared by the chemise and legacy collapsible
// branches (a11y: role=button + keyboard toggle).
function makeSubsectionFoldable(sec: HTMLElement): void {
  const heading = sec.querySelector<HTMLElement>('.project-preview-card__heading');
  if (!heading) return;
  heading.classList.add('project-preview-card__heading--toggle');
  heading.setAttribute('role', 'button');
  heading.setAttribute('tabindex', '0');
  const arrow = document.createElement('span');
  arrow.className = 'project-preview-card__subsection-arrow';
  heading.appendChild(arrow);
  let subOpen = true;
  const setSubOpen = (o: boolean): void => {
    subOpen = o;
    sec.classList.toggle('is-folded', !o);
    heading.setAttribute('aria-expanded', String(o));
  };
  setSubOpen(true);
  heading.addEventListener('click', () => setSubOpen(!subOpen));
  heading.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSubOpen(!subOpen); }
  });
}

function buildForgejoSlot(opts: ProjectPreviewCardOptions): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'project-preview-card__forgejo';
  wrap.setAttribute('data-component', 'magnify-forgejo-slot');

  // Already-pushed → link + next-steps panel (clone/cd/claude commands).
  // Operator feedback 2026-05-17 : "What is the next step une fois qu'on a
  // quitté le site pour Forgejo ?" — the panel closes that gap with
  // copy-on-click terminal commands.
  if (opts.forgejoRepoUrl) {
    renderForgejoSuccess(wrap, opts.forgejoRepoUrl, opts.forgejoCloneToken);
    return wrap;
  }

  // Idle → render the trigger button.
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'project-preview-card__forgejo-btn';
  btn.setAttribute('data-component', 'magnify-forgejo');
  btn.setAttribute('data-state', 'idle');
  btn.innerHTML = `<span data-component="magnify-forgejo-label">${escapeHtml(t('magnify.card.forgejo'))}</span>`;
  wrap.appendChild(btn);

  if (!opts.onPushForgejo) return wrap;

  const label = btn.querySelector<HTMLElement>('[data-component="magnify-forgejo-label"]')!;
  btn.addEventListener('click', async () => {
    if (btn.getAttribute('data-state') !== 'idle') return;
    const idleLabel = label.textContent ?? t('magnify.card.forgejo');
    btn.setAttribute('data-state', 'busy');
    label.textContent = t('magnify.card.forgejoBusy');
    try {
      const r = await opts.onPushForgejo!();
      // Swap the button out, render link + next-steps panel inline so
      // the user has terminal commands to copy without leaving Genesis.
      btn.remove(); // the cover toolbar may have relocated the button out of wrap
      renderForgejoSuccess(wrap, r.html_url, r.clone_token);
    } catch (err) {
      console.warn('[forgejo-bootstrap] push failed', err);
      btn.setAttribute('data-state', 'error');
      label.textContent = t('magnify.card.forgejoError');
      setTimeout(() => {
        btn.setAttribute('data-state', 'idle');
        label.textContent = idleLabel; // captured at click — short in cover mode
      }, 2400);
    }
  });
  return wrap;
}

// Renders the success state (link + next-steps panel) into the given wrap.
// Used both by the already-pushed initial mount + the post-click success
// path so the visual + functional surface stays in sync.
function renderForgejoSuccess(wrap: HTMLElement, htmlUrl: string, cloneToken?: string | null): void {
  const link = buildForgejoLink(htmlUrl);
  wrap.appendChild(link);
  wrap.appendChild(renderForgejoNextSteps({ htmlUrl, cloneToken }));
}

function buildForgejoLink(htmlUrl: string): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = 'project-preview-card__forgejo-link';
  a.setAttribute('data-component', 'magnify-forgejo-link');
  a.setAttribute('data-state', 'done');
  a.href = htmlUrl;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.setAttribute('aria-label', t('magnify.card.forgejoOpenAria'));
  a.textContent = t('magnify.card.forgejoDone');
  return a;
}

// Build a hover-tooltip describing the current version : kind + timestamp
// of every version, with a → arrow on the current one. The browser title
// attribute renders this as a native tooltip (zero CSS work, accessible).
function buildVersionTooltip(versions: ProjectVersionRow[], currentVersion: number): string {
  const sorted = [...versions].sort((a, b) => a.version_number - b.version_number);
  return sorted.map((v) => {
    const date = new Date(v.created_at);
    const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    const arrow = v.version_number === currentVersion ? '→ ' : '  ';
    const kindLabel = t(`version.kindShort.${v.context_kind}`);
    return `${arrow}v${v.version_number} (${kindLabel}) — ${time}`;
  }).join('\n');
}

function legacyCopy(text: string): boolean {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try { ok = document.execCommand('copy'); } catch { ok = false; }
  document.body.removeChild(ta);
  return ok;
}
